import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { creatorStreakFreezePath } from "@/routes"
import type { StreakFreezeSurface } from "@/types/streak"

/**
 * OKS-516 — Streak Freeze confirm dialog (UX Spec §2 + §4–§5).
 *
 * Wraps the live at-risk "Use a freeze" trigger. Confirm submits an Inertia
 * `<Form>` POST to `/streak/freeze` with an empty body — the server reads
 * everything from `Current.membership`. **NO shadcn `Form`/`FormField`, NO
 * RHF/zod** (Crew hard rule); the `AlertDialog` primitive is fine.
 *
 * # Server-side outcomes
 *
 * Success: server `redirect_to creator_streak_path` (303) → Inertia visit
 * re-renders `/streak` in the Frozen state (the at-risk card — and this
 * dialog with it — unmounts) → sonner toast from the success flash.
 *
 * Failure: server `redirect_back inertia: { errors: { base: t(...) } }`.
 * Inertia preserves the page; the dialog stays open and the render-prop
 * `errors.base` populates an inline destructive `<Alert>`. No coins charged.
 *
 * # Double-submit guard
 *
 * `disabled={processing}` + `aria-busy` on the confirm button; close is
 * locked while processing. The server is the real idempotency floor
 * (frozen_until guard under a row lock).
 */
interface StreakFreezeDialogProps {
  /** Current streak length, for the confirm copy. */
  days: number
  /** 🪙 cost of one freeze. */
  coinCost: number
  /** Freezes left this month (the post-spend count is `remaining - 1`). */
  remaining: number
}

export function StreakFreezeDialog({
  days,
  coinCost,
  remaining,
}: StreakFreezeDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // Count the creator will have AFTER this freeze (floored at 0).
  const remainingAfterSpend = Math.max(remaining - 1, 0)

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={
          <Button variant="secondary" data-testid="streak-at-risk-freeze">
            {t("streak.detail.cards.at_risk.freeze")}
          </Button>
        }
      />
      <AlertDialogContent data-testid="streak-freeze-dialog">
        <Form
          action={creatorStreakFreezePath()}
          method="post"
          options={{ preserveScroll: true }}
          // OKS-1023 — close explicitly on success. `router.post` resolves
          // `preserveState: true`, so the local `open` would otherwise
          // survive a same-page redirect; only the Frozen re-render
          // unmounting this card used to close it.
          onSuccess={() => setOpen(false)}
          // Empty body — server reads everything from Current.membership.
        >
          {({ processing, errors }) => (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle data-testid="streak-freeze-dialog-title">
                  {t("streak.detail.freeze.confirm.title")}
                </AlertDialogTitle>
                <AlertDialogDescription data-testid="streak-freeze-dialog-description">
                  {t("streak.detail.freeze.confirm.body", {
                    cost: coinCost,
                    days,
                    remaining: remainingAfterSpend,
                  })}
                </AlertDialogDescription>
              </AlertDialogHeader>

              {/* Server-side typed error → inline destructive Alert
                  (UX Spec §5 — don't auto-close on error). */}
              {errors.base && (
                <Alert
                  variant="destructive"
                  role="alert"
                  data-testid="streak-freeze-dialog-error"
                  className="mt-2"
                >
                  <AlertDescription>{errors.base}</AlertDescription>
                </Alert>
              )}

              <AlertDialogFooter>
                <AlertDialogCancel
                  type="button"
                  disabled={processing}
                  data-testid="streak-freeze-dialog-cancel"
                >
                  {t("streak.detail.freeze.confirm.cancel")}
                </AlertDialogCancel>
                {/* OKS-1023 — plain submit `Button`, NOT `AlertDialogAction`:
                    the Action is Base UI `Dialog.Close`, so the click would close
                    the dialog and unmount this `<Form>` with its `processing` and
                    `errors.base` before the server answers. */}
                <Button
                  type="submit"
                  disabled={processing}
                  aria-busy={processing}
                  data-testid="streak-freeze-dialog-confirm"
                >
                  {processing && (
                    <Loader2
                      aria-hidden="true"
                      className="mr-2 size-4 animate-spin"
                    />
                  )}
                  {t("streak.detail.freeze.confirm.submit")}
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * OKS-516 — At-risk freeze affordance. Renders the live confirm-dialog
 * trigger when `freeze.can_freeze`, otherwise a disabled CTA with a one-line
 * text helper carrying the reason (none-left / insufficient-coins). The
 * reason is conveyed by TEXT, never colour alone (UX Spec §6); the disabled
 * button is `aria-disabled` and describes itself via the helper line.
 */
export function StreakFreezeAction({
  freeze,
  days,
}: {
  freeze: StreakFreezeSurface
  days: number
}) {
  const { t } = useTranslation()

  if (freeze.can_freeze) {
    return (
      <StreakFreezeDialog
        days={days}
        coinCost={freeze.coin_cost}
        remaining={freeze.remaining}
      />
    )
  }

  const helperId = "streak-freeze-disabled-helper"
  const helper =
    freeze.disabled_reason === "insufficient_coins"
      ? t("streak.detail.freeze.disabled.insufficient", {
          cost: freeze.coin_cost,
        })
      : t("streak.detail.freeze.disabled.none_left")

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="secondary"
        disabled
        aria-disabled="true"
        aria-describedby={helperId}
        data-testid="streak-at-risk-freeze"
      >
        {t("streak.detail.cards.at_risk.freeze")}
      </Button>
      <p
        id={helperId}
        className="text-muted-foreground text-xs"
        data-testid="streak-at-risk-freeze-helper"
      >
        {helper}
      </p>
    </div>
  )
}
